import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  GoogleMap,
  LoadScript,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import toast from "react-hot-toast";

import { updateTournament } from "../../redux/slices/tournamentSlice";
import { DASHBOARD_STACK } from "../../utils/screenNames";
import ClubbieButton from "../../components/ClubbieButton/ClubbieButton";
import ClubbieInput from "../../components/ClubbieInput/ClubbieInput";
import ClubbieModal from "../../components/Modal/ClubbieModal";
import { tournamentWizardStepCompleted } from "../../utils/analytics";
import { WIZARD_STEP } from "../../utils/analyticsEvents";
import "./ChooseTournament.css";

const ChooseVenueType = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedTournament = useSelector(
    (state) => state.tournament.selectedTournament
  );
  const [isLoading, setIsLoading] = useState(false);
  const [venueName, setVenueName] = useState(
    selectedTournament?.matchVenue?.venue !== "HOME_TEAM"
      ? selectedTournament?.matchVenue?.venue || ""
      : ""
  );
  const [numberOfPitches, setNumberOfPitches] = useState(
    selectedTournament?.matchVenue?.numberOfPitches?.toString() || "1"
  );
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(
    selectedTournament?.matchVenue?.location || null
  );
  const [mapLink, setMapLink] = useState(
    selectedTournament?.matchVenue?.mapLink || ""
  );
  const [searchQuery, setSearchQuery] = useState(
    selectedTournament?.matchVenue?.venue || ""
  );
  const [selectedLocationName, setSelectedLocationName] = useState(
    selectedTournament?.matchVenue?.venue || ""
  );
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchInputRef = useRef(null);

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    if (selectedTournament?.matchVenue) {
      setVenueName(
        selectedTournament.matchVenue.venue !== "HOME_TEAM"
          ? selectedTournament.matchVenue.venue || ""
          : ""
      );
      setNumberOfPitches(
        selectedTournament.matchVenue.numberOfPitches?.toString() || "1"
      );
      setSelectedLocation(selectedTournament.matchVenue.location || null);
      setMapLink(selectedTournament.matchVenue.mapLink || "");
      setSearchQuery(selectedTournament.matchVenue.venue || "");
      setSelectedLocationName(selectedTournament.matchVenue.venue || "");
    }
  }, [selectedTournament]);

  useEffect(() => {
    if (showMapModal && searchInputRef.current) {
      searchInputRef?.current?.focus();
    }
  }, [showMapModal]);

  const onPlaceSelected = (place) => {
    if (!place.geometry || !place.geometry.location) return;

    const location = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    setSelectedLocation(location);
    setMapLink(`https://www.google.com/maps?q=${location.lat},${location.lng}`);
    setSearchQuery(place.formatted_address);
    setSelectedLocationName(place.formatted_address);

    // Center map on selected location
    if (mapRef.current) {
      mapRef.current.panTo(location);
      mapRef.current.setZoom(15);
    }
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setSelectedLocation({ lat, lng });
    setMapLink(`https://www.google.com/maps?q=${lat},${lng}`);

    // Reverse geocode to get the address
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setSelectedLocationName(results[0].formatted_address);
      }
    });
  };

  const handleNumberOfPitchesChange = (e) => {
    const value = e.target.value;
    // Only allow positive numbers
    if (value === "" || (parseInt(value) > 0 && /^\d+$/.test(value))) {
      setNumberOfPitches(value);
    }
  };

  const handleNext = async () => {
    try {
      setIsLoading(true);
      // Validate venue name
      if (!venueName.trim()) {
        toast.error("Please enter venue name");
        return;
      }

      // Validate location
      if (!selectedLocation) {
        toast.error("Please select a location on the map");
        return;
      }

      let updateData = {
        matchVenue: {
          numberOfPitches: parseInt(numberOfPitches) || 1,
          venue: venueName.trim(),
          location: selectedLocation,
          address: selectedLocationName || "",
          mapLink: mapLink,
        },
      };

      const result = await dispatch(
        updateTournament({
          id: selectedTournament?.id,
          updateData,
        })
      ).unwrap();

      if (result) {
        tournamentWizardStepCompleted({
          stepName: WIZARD_STEP.CHOOSE_VENUE_TYPE,
          tournamentId: selectedTournament?.id,
        });
        toast.success("Venue details updated successfully!");
        navigate(DASHBOARD_STACK.CREATE_TOURNAMENT.CHOOSE_TOURNAMENT);
      } else {
        throw new Error("Failed to update venue details");
      }
    } catch (error) {
      toast.error(error.message || "Failed to update venue details");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="input-section">
      <p style={{ fontSize: "18px" }}>Add Your Venue Details</p>
      <div className="tournaments-container">
        <div className="form-group">
          <label>Venue Name:</label>
          <ClubbieInput
            type="text"
            value={venueName}
            onChange={(e) => setVenueName(e.target.value)}
            placeholder="Enter venue name"
          />
        </div>
        <div className="form-group">
          <label>Venue Location:</label>
          {selectedLocationName && (
            <div className="selected-location-name">
              {selectedLocationName}{" "}
              {selectedLocation && (
                <a
                  href={mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-link ml-2"
                >
                  View on Google Maps
                </a>
              )}{" "}
            </div>
          )}
          <ClubbieButton
            onClick={() => {
              setShowMapModal(true);
              setSearchQuery(venueName);
              setTimeout(() => {
                searchInputRef?.current?.focus();
              }, 100);
            }}
          >
            Pick Location on Map
          </ClubbieButton>
        </div>
      </div>

      <ClubbieButton
        className="next-button btn btn-info"
        onClick={handleNext}
        disabled={isLoading || !venueName || !selectedLocation}
      >
        {isLoading ? "Updating..." : "Next"}
      </ClubbieButton>

      <ClubbieModal
        isOpen={showMapModal}
        toggle={() => setShowMapModal(false)}
        title=""
        minHeight="650px"
        maxHeight=""
      >
        <div className="map-modal-content">
          <div className="search-box-container">
            <LoadScript
              googleMapsApiKey={GOOGLE_MAPS_API_KEY}
              libraries={["places"]}
            >
              <Autocomplete
                onLoad={(autocomplete) => {
                  autocompleteRef.current = autocomplete;
                }}
                onPlaceChanged={() => {
                  if (autocompleteRef.current) {
                    const place = autocompleteRef.current.getPlace();
                    onPlaceSelected(place);
                  }
                }}
                options={{
                  componentRestrictions: { country: "gb" },
                  types: ["address"],
                }}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search for a location"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="location-search-input"
                />
              </Autocomplete>
            </LoadScript>
          </div>
          <p>Click on the map or search for a location to select the venue</p>
          <LoadScript
            googleMapsApiKey={GOOGLE_MAPS_API_KEY}
            libraries={["places"]}
          >
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "250px" }}
              center={selectedLocation || { lat: 51.5074, lng: -0.1278 }}
              zoom={selectedLocation ? 15 : 10}
              onClick={handleMapClick}
              onLoad={(map) => {
                mapRef.current = map;
              }}
            >
              {selectedLocation && <Marker position={selectedLocation} />}
            </GoogleMap>
          </LoadScript>
        </div>
        <div className="text-end mt-2">
          <button
            className="btn btn-primary"
            onClick={() => setShowMapModal(false)}
          >
            Done
          </button>
        </div>
      </ClubbieModal>
    </div>
  );
};

export default ChooseVenueType;
